import React, { type FC } from 'react';
import { MessageModalLayout, Text, Box } from '@wix/design-system';
import * as panels from '#packages/panels';
import { connectWithScope } from '#packages/apilib';
import { RtlLtrSettingsManagerScope } from './rtlLtrSettingsManagerScope';
import { biLogger } from '#packages/util';
import * as symbols from '@wix/santa-editor-symbols';
import { translate } from '#packages/i18n';
import { clickOnPanel } from '@wix/bi-logger-editor/v2';

interface ChangeRtlLtrSettingsDispatchProps {
  onCloseButtonClick: () => void;
  primaryButtonOnClick: () => void;
  secondaryButtonOnClick: () => void;
}

interface ChangeRtlLtrSettingsOwnProps {
  panelName: string;
  onApprove: () => void;
  onClose: () => void;
  flow: string;
}

type ChangeRtlLtrSettingsProps = ChangeRtlLtrSettingsDispatchProps &
  ChangeRtlLtrSettingsOwnProps;

export const ChangeRtlLtrSettingsPanelPure: FC<ChangeRtlLtrSettingsProps> = ({
  onCloseButtonClick,
  primaryButtonOnClick,
  secondaryButtonOnClick,
  panelName,
}) => {
  return (
    <panels.frames.FocusPanelFrame shouldHideHeader panelName={panelName}>
      <MessageModalLayout
        illustration={
          <symbols.symbol name="changeRtlLtrSettingsPanel_Illustration" />
        }
        onCloseButtonClick={onCloseButtonClick}
        primaryButtonOnClick={primaryButtonOnClick}
        secondaryButtonOnClick={secondaryButtonOnClick}
        primaryButtonText={translate(
          'DOMOrder_directionChangeModal_button_change',
        )}
        secondaryButtonText={translate(
          'DOMOrder_directionChangeModal_button_notNow',
        )}
        title={translate('DOMOrder_directionChangeModal_title')}
        content={
          <Box direction="vertical">
            <Text>{translate('DOMOrder_directionChangeModal_text')}</Text>
            <Text>{translate('DOMOrder_directionChangeModal_text2')}</Text>
          </Box>
        }
        footnote={
          <Text size="small">
            {translate('DOMOrder_directionChangeModal_note')}
          </Text>
        }
        dataHook="rtl-ltr-message-modal"
      />
    </panels.frames.FocusPanelFrame>
  );
};

const mapDispatchToProps = (
  scope: RtlLtrSettingsManagerScope,
  ownProps: ChangeRtlLtrSettingsOwnProps,
): ChangeRtlLtrSettingsDispatchProps => {
  const { editorAPI } = scope;
  const closePanel = () => {
    editorAPI.panelManager.closePanelByName(ownProps.panelName);
    ownProps.onClose();
  };

  const sendClickOnPanelEvent = (actionName: string) => {
    biLogger.report(
      clickOnPanel({
        panel_name: ownProps.panelName,
        action_name: actionName,
        flow: ownProps.flow,
      }),
    );
  };

  return {
    onCloseButtonClick: () => {
      closePanel();
      sendClickOnPanelEvent('close_button');
    },
    primaryButtonOnClick: () => {
      closePanel();
      ownProps.onApprove();
      sendClickOnPanelEvent('primary_button');
    },
    secondaryButtonOnClick: () => {
      closePanel();
      sendClickOnPanelEvent('secondary_button');
    },
  };
};

export const ChangeRtlLtrSettingsPanel = connectWithScope(
  () => RtlLtrSettingsManagerScope,
  ChangeRtlLtrSettingsPanelPure,
  undefined,
  mapDispatchToProps,
);
